import React, { useState } from "react"
import { X, Sparkles } from "lucide-react"

const stripHtml = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()

const getCsrf = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

const sendFeedback = (actionType, rawInput, finalText, language, tone) => {
  fetch("/book/ai/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrf() },
    body: JSON.stringify({ action_type: actionType, raw_input: rawInput, final_text: finalText, language, tone }),
  }).catch(() => {})
}

const TONES = [
  { value: "business_standard", label: "Business standard" },
  { value: "soft",              label: "Soft / polite" },
  { value: "urgent",            label: "Urgent" },
  { value: "short",             label: "Short" },
  { value: "detailed",          label: "Detailed" },
  { value: "apologetic",        label: "Apologetic" },
  { value: "neutral",           label: "Neutral" },
  { value: "premium",           label: "Premium" },
]

const LANGUAGES = [
  { value: "ua", label: "Ukrainian" },
  { value: "en", label: "English" },
]

const SmartReplyPopover = ({ replyTo, bundleId, onInsert, onClose }) => {
  const [tone, setTone]       = useState("business_standard")
  const [language, setLang]   = useState("ua")
  const [result, setResult]   = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState("")

  const replyToText = stripHtml(replyTo.text || "")

  const run = async () => {
    setLoading(true)
    setError("")
    setResult("")
    try {
      const res = await fetch("/book/ai/assist", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrf() },
        body: JSON.stringify({
          action_type:    "smart_reply",
          reply_to_text:  replyToText,
          tone,
          language,
          bundle_id:      bundleId,
        }),
      })
      const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }))
      if (data.error) setError(data.error)
      else setResult(typeof data.result === "object" ? JSON.stringify(data.result, null, 2) : data.result)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="absolute bottom-full left-0 right-0 z-30 bg-white border border-slate-200 rounded-xl shadow-xl p-3 mb-1 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-blue-500" /> AI Reply
        </span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
        >
          {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          value={language}
          onChange={(e) => setLang(e.target.value)}
          className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
        >
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={run}
          disabled={loading}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg disabled:opacity-50 transition-colors"
        >
          {loading ? "Thinking…" : "Generate reply"}
        </button>
        {result && (
          <button
            type="button"
            onClick={() => {
              sendFeedback("smart_reply", replyToText, result, language, tone)
              onInsert(result)
              onClose()
            }}
            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
          >
            Insert
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {result && (
        <textarea
          value={result}
          onChange={(e) => setResult(e.target.value)}
          rows={4}
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      )}
    </div>
  )
}

export default SmartReplyPopover
